import React from 'react';
import { Eye, Mail, MailCheck, MessageSquare, MessageSquareCheck, MessageSquarePlus, ThumbsUp, UserCheck, UserPlus } from 'lucide-react';
import { SequenceStepType } from '../../../../../interfaces/models/campaign';

export type PerformanceMetric =
  | 'connectionRequestsSent'
  | 'linkedinMessagesSent'
  | 'inmailsSent'
  | 'emailsSent'
  | 'connectionsAccepted'
  | 'linkedinReplies'
  | 'inmailReplies'
  | 'emailReplies'
  | 'profileVisits'
  | 'postLikes'
  | 'postComments';

export type IPerformanceMetrics = Record<PerformanceMetric, number>;

export interface IPerformanceBucket extends IPerformanceMetrics {
  /** YYYY-MM-DD for daily buckets, YYYY-MM for monthly ones. */
  key: string;
}

export interface IWorkspacePerformance {
  granularity: 'day' | 'month';
  from: string;
  to: string;
  totals: IPerformanceMetrics;
  series: IPerformanceBucket[];
}

export interface ISenderPerformanceRow extends IPerformanceMetrics {
  identityId: string;
  name: string;
  createdBy: string | null;
  /** The viewer's own LinkedIn profile. */
  isMe: boolean;
  campaignCount: number;
}

/** LinkedIn's "in" mark — lucide ships no brand icons. */
const LinkedInBox = ({ size = 16, strokeWidth = 1.5 }: { size?: number | string; strokeWidth?: number | string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="3" width="18" height="18" rx="4" />
    <path d="M8 11v5" />
    <path d="M8 8v.01" />
    <path d="M12 16v-5" />
    <path d="M16 16v-3a2 2 0 0 0-4 0" />
  </svg>
);

export interface IMetricConfig {
  key: PerformanceMetric;
  label: string;
  /** Line, icon and card accent — the chart reuses it so a line matches its card. */
  color: string;
  iconBg: string;
  iconBorder: string;
  Icon: React.ComponentType<{ size?: number | string; strokeWidth?: number | string }>;
}

// Two rows of four, as in the design: what went out, then what came back.
export const METRICS: IMetricConfig[] = [
  { key: 'connectionRequestsSent', label: 'Connection requests sent', color: '#F79009', iconBg: 'rgba(247, 144, 9, 0.12)', iconBorder: 'rgba(247, 144, 9, 0.3)', Icon: UserPlus },
  { key: 'linkedinMessagesSent', label: 'LinkedIn messages sent', color: '#EE46BC', iconBg: 'rgba(238, 70, 188, 0.12)', iconBorder: 'rgba(238, 70, 188, 0.3)', Icon: MessageSquarePlus },
  { key: 'inmailsSent', label: 'Open Profile / InMails sent', color: '#4E5BA6', iconBg: 'rgba(78, 91, 166, 0.12)', iconBorder: 'rgba(78, 91, 166, 0.3)', Icon: LinkedInBox },
  { key: 'emailsSent', label: 'Emails sent', color: '#2E90FA', iconBg: 'rgba(46, 144, 250, 0.12)', iconBorder: 'rgba(46, 144, 250, 0.3)', Icon: Mail },
  { key: 'connectionsAccepted', label: 'Connections accepted', color: '#6172F3', iconBg: 'rgba(97, 114, 243, 0.12)', iconBorder: 'rgba(97, 114, 243, 0.3)', Icon: UserCheck },
  { key: 'linkedinReplies', label: 'LinkedIn replies', color: '#17B26A', iconBg: 'rgba(23, 178, 106, 0.12)', iconBorder: 'rgba(23, 178, 106, 0.3)', Icon: MessageSquareCheck },
  { key: 'inmailReplies', label: 'Open Profile / InMail replies', color: '#7A5AF8', iconBg: 'rgba(122, 90, 248, 0.12)', iconBorder: 'rgba(122, 90, 248, 0.3)', Icon: LinkedInBox },
  { key: 'emailReplies', label: 'Email replies', color: '#28CCD1', iconBg: '#E1FFFD', iconBorder: '#28CCD1', Icon: MailCheck },
];

/** Warm-up steps — only a single campaign's analytics shows these, and only if it uses them. */
export const ENGAGEMENT_METRICS: IMetricConfig[] = [
  { key: 'profileVisits', label: 'Profile visits', color: '#0DBD39', iconBg: 'rgba(13, 189, 57, 0.12)', iconBorder: 'rgba(13, 189, 57, 0.3)', Icon: Eye },
  { key: 'postLikes', label: 'Post likes', color: '#BB4D00', iconBg: 'rgba(187, 77, 0, 0.12)', iconBorder: 'rgba(187, 77, 0, 0.3)', Icon: ThumbsUp },
  { key: 'postComments', label: 'Post comments', color: '#FDBE00', iconBg: 'rgba(253, 190, 0, 0.12)', iconBorder: 'rgba(253, 190, 0, 0.3)', Icon: MessageSquare },
];

export const ALL_METRICS: IMetricConfig[] = [...METRICS, ...ENGAGEMENT_METRICS];

/** The cards each sequence step produces: its send, and the response it can get. */
const METRICS_BY_STEP: Record<SequenceStepType, PerformanceMetric[]> = {
  connection_request: ['connectionRequestsSent', 'connectionsAccepted'],
  message: ['linkedinMessagesSent', 'linkedinReplies'],
  sn_message: ['inmailsSent', 'inmailReplies'],
  email: ['emailsSent', 'emailReplies'],
  profile_visit: ['profileVisits'],
  post_like: ['postLikes'],
  post_comment: ['postComments'],
};

/** Only the stats for actions the campaign's sequence actually uses, in the usual order. */
export function metricsForSequence(stepTypes: SequenceStepType[]): IMetricConfig[] {
  const used = new Set(stepTypes.flatMap(t => METRICS_BY_STEP[t] ?? []));
  return ALL_METRICS.filter(m => used.has(m.key));
}

export const emptyMetrics = (): IPerformanceMetrics =>
  Object.fromEntries(ALL_METRICS.map(m => [m.key, 0])) as IPerformanceMetrics;
